import { fetchData } from "../clients/api";
import { handleError } from "../errors";
import { Result } from "./types";
import { EstimateGroupingUserSetting } from "../interfaces/UserSettings";
import { User } from "../interfaces/User";

const fetchRecentlyViewedProjectIds = async (): Promise<Result<string[]>> => {
  try {
    const recentlyViewedResponse = await fetchData({
      table: "settings",
      id: "RECENTLY_VIEWED_PROJECTS%7CRECENTLY_VIEWED_PROJECTS",
    });
    const recentlyViewedIds = recentlyViewedResponse?.data?.value || [];
    return { success: true, data: recentlyViewedIds };
  } catch (error) {
    const err = handleError(error, {
      table: "settings",
      settingKey: "RECENTLY_VIEWED_PROJECTS",
      context: "fetchRecentlyViewedProjectIds",
    });
    return { success: false, error: err };
  }
};

const fetchPinnedProjectIds = async (): Promise<Result<string[]>> => {
  try {
    const pinnedResponse = await fetchData({
      table: "settings",
      id: "PINNED_PROJECTS%7CPINNED_PROJECTS",
    });
    const pinnedIds = pinnedResponse?.data?.value || [];
    return { success: true, data: pinnedIds };
  } catch (error) {
    const err = handleError(error, {
      table: "settings",
      settingKey: "PINNED_PROJECTS",
      context: "fetchPinnedProjectIds",
    });
    return { success: false, error: err };
  }
};

const fetchEstimateGroupings = async (estimateId: string): Promise<Result<EstimateGroupingUserSetting>> => {
  try {
    const response = await fetchData({
      table: "settings",
      id: estimateId + "%7CREPORT_GROUP_FIELDS",
    });
    return { success: true, data: response };
  } catch (error) {
    const err = handleError(error, {
      table: "settings",
      settingKey: "REPORT_GROUP_FIELDS",
      context: "fetchEstimateGroupings",
    });
    return { success: false, error: err };
  }
};

const fetchSOVGroupings = async (userId: string): Promise<Result<any>> => {
  try {
    const response = await fetchData({
      table: "settings",
      id: userId + "%7CSOV_GROUP_FIELD",
    });
    return { success: true, data: response };
  } catch (error) {
    const err = handleError(error, {
      table: "settings",
      settingKey: "SOV_GROUP_FIELD",
      userId,
      context: "fetchSOVGroupings",
    });
    return { success: false, error: err };
  }
};

const fetchVMGroupings = async (estimateId: string): Promise<Result<EstimateGroupingUserSetting>> => {
  try {
    const response = await fetchData({
      table: "settings",
      id: estimateId + "%7CVM_DECISIONS_LIST_GRID_GROUPING",
    });
    return { success: true, data: response };
  } catch (error) {
    const err = handleError(error, {
      table: "settings",
      settingKey: "VM_DECISIONS_LIST_GRID_GROUPING",
      context: "fetchVMGroupings",
    });
    return { success: false, error: err };
  }
};

const fetchUserProfile = async (): Promise<Result<{ user: User }>> => {
  try {
    const response = await fetchData({ table: "profile" });
    if (!response?.user) {
      throw new Error("User profile data is missing in the response.");
    }
    return { success: true, data: { user: response.user } };
  } catch (error) {
    const err = handleError(error, { table: "profile", context: "fetchUserProfile" });
    return { success: false, error: err };
  }
};

const fetchUnitSort = async (estimateId: string): Promise<Result<any>> => {
  try {
    const response = await fetchData({
      table: "settings",
      id: estimateId + "%7CUNITS_TOOLBAR_ORDER_UNITS_BY",
    });
    return { success: true, data: response };
  } catch (error) {
    const err = handleError(error, {
      table: "settings",
      settingKey: "UNITS_TOOLBAR_ORDER_UNITS_BY",
      estimateId,
      context: "fetchUnitSort",
    });
    return { success: false, error: err };
  }
};

export {
  fetchRecentlyViewedProjectIds,
  fetchPinnedProjectIds,
  fetchEstimateGroupings,
  fetchSOVGroupings,
  fetchVMGroupings,
  fetchUserProfile,
  fetchUnitSort,
};
